import {ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal} from '@angular/core';
import {DatePipe, formatDate} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {rxResource} from '@angular/core/rxjs-interop';
import {firstValueFrom} from 'rxjs';
import {MessageService, PrimeTemplate} from 'primeng/api';
import {Button} from 'primeng/button';
import {DatePicker} from 'primeng/datepicker';
import {TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';

import {SupportEmailClient} from '../../api-client/api/api';
import {PastSupportEmailDto} from '../../api-client/model/past-support-email-dto';
import {PastSupportEmailsDto} from '../../api-client/model/past-support-emails-dto';
import {SubmitButtonComponent} from '../../shared/components/submit-button/submit-button.component';
import {TypedTableRowDirective} from '../../shared/typed-table-row.directive';
import {DATE_FORMAT} from '../../shared/formats';

// The API draws the day's boundaries in New York, so the page shows times there too.
const NEW_YORK = 'America/New_York';

const newYorkTime = new Intl.DateTimeFormat('en-GB', {timeZone: NEW_YORK, hour: '2-digit', minute: '2-digit'});

// Today's date in New York, as a local Date for the picker.
function newYorkToday(): Date {
  const [year, month, day] = new Date().toLocaleDateString('en-CA', {timeZone: NEW_YORK}).split('-').map(Number);
  return new Date(year, month - 1, day);
}

function emailCount(count: number): string {
  return count === 1 ? '1 email' : `${count} emails`;
}

@Component({
  selector: 'homey-past-emails-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    DatePicker,
    PrimeTemplate,
    TableModule,
    Tag,
    Button,
    SubmitButtonComponent,
    TypedTableRowDirective,
  ],
  templateUrl: './past-emails-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PastEmailsPageComponent {
  private readonly supportEmailClient = inject(SupportEmailClient);
  private readonly messageService = inject(MessageService);

  protected readonly dateFormat = DATE_FORMAT;
  protected readonly emailCount = emailCount;
  protected readonly today = newYorkToday();
  protected readonly day = signal<Date | null>(this.today);

  // The picked day as a string, so picking the same day again doesn't reload it.
  private readonly isoDay = computed(() => {
    const day = this.day();
    return day ? formatDate(day, 'yyyy-MM-dd', 'en-US') : undefined;
  });

  // Reloads when the day changes; a load for a day no longer picked is dropped.
  protected readonly list = rxResource<PastSupportEmailsDto, string | undefined>({
    params: () => this.isoDay(),
    stream: ({params}) => this.supportEmailClient.listPastSupportEmails(params),
  });

  // `list.value()` throws while the load is in its error state, so it is only read behind `hasValue()`.
  protected readonly emails = computed(() => (this.list.hasValue() ? this.list.value().emails : undefined));
  protected readonly draftedCount = computed(() => this.emails()?.filter(email => email.drafted).length ?? 0);

  protected readonly selected = linkedSignal<string | undefined, PastSupportEmailDto[]>({
    source: this.isoDay,
    computation: () => [],
  });
  // Queued from this page, so they can't be picked again before the processor drafts them.
  protected readonly queued = signal<ReadonlySet<string>>(new Set());

  protected readonly isSelectable = ({data}: {data: PastSupportEmailDto}): boolean =>
    !data.drafted && !this.queued().has(data.messageId);

  // PrimeNG enables the header checkbox whenever the table has rows, even if none can be ticked.
  protected readonly noneSelectable = computed(() => !this.emails()?.some(email => this.isSelectable({data: email})));

  protected readonly draft = async (): Promise<void> => {
    const messageIds = this.selected().map(email => email.messageId);
    const {queued} = await firstValueFrom(this.supportEmailClient.draftPastSupportEmails({messageIds}));
    this.queued.update(current => new Set([...current, ...messageIds]));
    this.selected.set([]);
    this.messageService.add({
      severity: 'success',
      summary: `Queued ${emailCount(queued)}`,
      detail: 'Drafts appear in Zoho over the next few minutes, for the emails a category fits.',
    });
  };

  protected receivedTime(email: PastSupportEmailDto): string {
    return newYorkTime.format(new Date(email.receivedAt));
  }
}
